import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  MusicNote, Edit, Delete, Add, Person, TableChart, ArrowForward,
  Send as SendIcon, HourglassEmpty as PendingIcon, CheckCircle as ApprovedIcon, Cancel as RejectedIcon,
  VisibilityOff, Visibility,
} from '@mui/icons-material';
import {
  Container, Typography, Grid, Button, Box, Card, CardContent, CardMedia,
  CardActions, List, ListItem, ListItemText, Chip, CircularProgress, Alert,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import axiosInstance from '../../utils/axiosConfig';
import AvailableOpportunities from '../../components/AvailableOpportunities';
import { NavigatorFormData } from '../../types/NavigatorTypes';

const stripHtml = (html: string): string => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const bandSizeOptions = [
  { label: 'Soloist', min: 1, max: 1 },
  { label: 'Duo', min: 2, max: 2 },
  { label: 'Trio', min: 3, max: 3 },
  { label: 'Quartet', min: 4, max: 4 },
  { label: 'Quintet', min: 5, max: 5 },
  { label: 'Sextet', min: 6, max: 6 },
  { label: 'Septet', min: 7, max: 7 },
  { label: 'Octet', min: 8, max: 8 },
  { label: 'Nonet', min: 9, max: 9 },
  { label: 'Big Band', min: 10, max: Infinity },
];

function getSizeLabel(total: number): string {
  return bandSizeOptions.find(o => total >= o.min && total <= o.max)?.label ?? `${total} members`;
}

interface Band {
  _id: string;
  artistName: string;
  mainImage?: string;
  additionalImages?: string[];
  status?: string;
  isDraft?: boolean;
  isDeactivated?: boolean;
  publishedTo?: string[];
  createdAt?: string;
  updatedAt?: string;
  genres?: string[];
  maleMembers?: number;
  femaleMembers?: number;
  about?: string;
  externalValidation?: { source: string; quote?: string }[];
}

interface Application {
  _id: string;
  type: 'navigator' | 'other-opportunity-types';
  status: 'draft' | 'submitted';
  lastSaved?: Date;
  submittedAt?: Date;
  deadline: Date;
  formData: NavigatorFormData;
}

interface StagePlot {
  _id: string;
  name: string;
  description?: string;
  bandId?: string;
  createdAt: Date;
  updatedAt: Date;
}

const ArtistDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bands, setBands] = useState<Band[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bandToDelete, setBandToDelete] = useState<Band | null>(null);
  const [applications, setApplications] = useState<Application[]>([]);
  const [appDeleteDialogOpen, setAppDeleteDialogOpen] = useState(false);
  const [appToDelete, setAppToDelete] = useState<Application | null>(null);
  const [stagePlots, setStagePlots] = useState<StagePlot[]>([]);
  const [appearingIn, setAppearingIn] = useState<Band[]>([]);

  useEffect(() => {
    const fetchBands = async (userId: string) => {
      try {
        setLoading(true);
        try {
          const inherited = await axiosInstance.post('/api/users/inherit-bands');
          if (inherited.data.inherited > 0) {
            console.log(`Inherited ${inherited.data.inherited} band(s) from admin records`);
          }
        } catch { /* non-fatal */ }
        const response = await axiosInstance.get<Band[]>(`/api/users/${userId}/bands`);
        setBands(response.data);
        setError(null);
      } catch {
        setError('Failed to load bands');
      } finally {
        setLoading(false);
      }
    };
    if (user?._id) fetchBands(user._id);
  }, [user]);

  useEffect(() => {
    axiosInstance.get('/api/applications/drafts')
      .then(r => setApplications(r.data.map((d: any) => ({ ...d, status: 'draft' }))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    axiosInstance.get('/api/stageplots')
      .then(r => setStagePlots(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user?._id) {
      axiosInstance.get<Band[]>(`/api/users/${user._id}/appearing-in`)
        .then(r => setAppearingIn(r.data))
        .catch(() => {});
    }
  }, [user]);

  const handleDeleteClick = (e: React.MouseEvent, band: Band) => {
    e.stopPropagation();
    setBandToDelete(band);
    setDeleteDialogOpen(true);
  };

  const handleDeleteApplicationClick = (application: Application) => {
    setAppToDelete(application);
    setAppDeleteDialogOpen(true);
  };

  const handleDeleteApplicationConfirm = async () => {
    if (!appToDelete) return;
    try {
      await axiosInstance.delete(`/api/applications/drafts/${appToDelete._id}`);
      setApplications(prev => prev.filter(a => a._id !== appToDelete._id));
      setAppDeleteDialogOpen(false);
      setAppToDelete(null);
    } catch {
      setError('Failed to delete application');
    }
  };

  const handleDeleteStagePlot = async (plotId: string) => {
    if (window.confirm('Are you sure you want to delete this stage plot?')) {
      try {
        await axiosInstance.delete(`/api/stageplots/${plotId}`);
        setStagePlots(plots => plots.filter(p => p._id !== plotId));
      } catch { /* ignore */ }
    }
  };

  const handleDeleteConfirm = async () => {
    if (!bandToDelete) return;
    try {
      await axiosInstance.delete(`/api/bandrecommendations/${bandToDelete._id}`);
      setBands(bands.filter(b => b._id !== bandToDelete._id));
      setStagePlots(plots => plots.filter(p => p.bandId !== bandToDelete._id));
      setDeleteDialogOpen(false);
      setBandToDelete(null);
    } catch {
      setError('Failed to delete band');
    }
  };

  const handleSubmitForReview = async (band: Band) => {
    try {
      await axiosInstance.patch(`/api/bandrecommendations/${band._id}/submit`);
      setBands(prev => prev.map(b => b._id === band._id ? { ...b, isDraft: false, status: 'pending' } : b));
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to submit band for review. Please try again.');
    }
  };

  const handleToggleDeactivate = async (e: React.MouseEvent, band: Band) => {
    e.stopPropagation();
    const action = band.isDeactivated ? 'activate' : 'deactivate';
    try {
      await axiosInstance.patch(`/api/bandrecommendations/${band._id}/${action}`);
      setBands(prev => prev.map(b => b._id === band._id ? { ...b, isDeactivated: !b.isDeactivated } : b));
    } catch {
      setError(`Failed to ${action} band. Please try again.`);
    }
  };

  const firstName = user?.name?.split(' ')[0] || null;
  const isFirstVisit = !loading && bands.length === 0 && applications.length === 0;

  const stagePlotForBand = (bandId: string) => stagePlots.find(p => p.bandId === bandId) || null;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* ── Welcome banner ── */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          minHeight: { xs: 160, md: 200 },
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {user?.profileImage ? (
          <>
            <Box
              component="img"
              src={user.profileImage}
              alt=""
              sx={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%',
                objectFit: 'cover',
                objectPosition: user.profileImagePosition || 'center center',
                filter: 'brightness(0.35)',
              }}
            />
            <Box sx={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(90deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.4) 60%, rgba(0,0,0,0.1) 100%)',
            }} />
          </>
        ) : (
          <Box sx={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(135deg, #0a0a1a 0%, #0d1b3e 60%, #0a1f12 100%)',
          }} />
        )}

        <Container maxWidth="lg" sx={{ position: 'relative', py: { xs: 4, md: 5 }, px: { xs: 3, md: 6 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              {user?.profileImage ? (
                <Box
                  component="img"
                  src={user.profileImage}
                  alt={user.name}
                  sx={{
                    width: { xs: 56, md: 80 },
                    height: { xs: 56, md: 80 },
                    borderRadius: '50%',
                    objectFit: 'cover',
                    objectPosition: user.profileImagePosition || 'center center',
                    border: '3px solid rgba(255,255,255,0.25)',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <Box sx={{
                  width: { xs: 56, md: 80 },
                  height: { xs: 56, md: 80 },
                  borderRadius: '50%',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  border: '2px dashed rgba(255,255,255,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Person sx={{ fontSize: 32, color: 'rgba(255,255,255,0.3)' }} />
                </Box>
              )}
              <Box>
                <Typography
                  variant="h3"
                  fontWeight={700}
                  color="white"
                  sx={{ fontSize: { xs: '1.6rem', md: '2.25rem' }, mb: 0.25, lineHeight: 1.15 }}
                >
                  {isFirstVisit
                    ? `Welcome${firstName ? `, ${firstName}` : ''}.`
                    : `Welcome back${firstName ? `, ${firstName}` : ''}.`}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                  {isFirstVisit
                    ? 'Start by creating your first band or filling in your profile.'
                    : `${bands.length} band${bands.length !== 1 ? 's' : ''}`}
                </Typography>
              </Box>
            </Box>

            {user?.name && (
              <Button
                component={RouterLink}
                to={`/musician/${encodeURIComponent(user.name)}`}
                variant="outlined"
                endIcon={<ArrowForward />}
                sx={{
                  color: 'rgba(255,255,255,0.85)',
                  borderColor: 'rgba(255,255,255,0.3)',
                  '&:hover': { borderColor: 'white', color: 'white', bgcolor: 'rgba(255,255,255,0.08)' },
                  flexShrink: 0,
                }}
              >
                View My Page
              </Button>
            )}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {/* ── Primary action blobs ── */}
        <Grid container spacing={2} sx={{ mb: 5 }}>
          {[
            {
              icon: <MusicNote sx={{ color: 'white', fontSize: 22 }} />,
              iconBg: 'primary.main',
              title: 'Your Bands',
              description: 'Submit a band for imc.show platform, and other IMC activities. This is a platform that IMC uses for promoting your work.',
              buttonLabel: 'Create Band',
              buttonVariant: 'contained' as const,
              to: '/submit',
              accent: 'rgba(25,118,210,0.06)',
              borderColor: 'primary.main',
              shadow: 'rgba(25,118,210,0.2)',
            },
            {
              icon: <TableChart sx={{ color: 'white', fontSize: 22 }} />,
              iconBg: 'secondary.main',
              title: 'Stage Plots',
              description: 'Create a technical rider and stage layout for any of your bands to share with venues.',
              buttonLabel: 'New Stage Plot',
              buttonVariant: 'outlined' as const,
              to: '/artist/stage-plots/new',
              accent: 'transparent',
              borderColor: 'divider',
              shadow: 'rgba(0,0,0,0.15)',
            },
            {
              icon: <Person sx={{ color: 'white', fontSize: 22 }} />,
              iconBg: 'success.main',
              title: 'Your Profile',
              description: 'Your public musician page — bio, genres, instruments and social links. Your bands will appear here.',
              buttonLabel: 'Edit Profile',
              buttonVariant: 'outlined' as const,
              to: '/artist/profile',
              accent: 'rgba(46,125,50,0.06)',
              borderColor: 'divider',
              shadow: 'rgba(46,125,50,0.15)',
            },
          ].map((blob) => (
            <Grid item xs={12} sm={4} key={blob.title}>
              <Card
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  border: '1px solid',
                  borderColor: blob.borderColor,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${blob.accent} 0%, transparent 100%)`,
                  transition: 'box-shadow 0.2s ease',
                  '&:hover': { boxShadow: `0 4px 24px ${blob.shadow}` },
                }}
              >
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Box sx={{
                      width: 44, height: 44, borderRadius: '50%',
                      bgcolor: blob.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      {blob.icon}
                    </Box>
                    <Typography variant="h6" fontWeight={700}>{blob.title}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                    {blob.description}
                  </Typography>
                </Box>
                <Button
                  component={RouterLink}
                  to={blob.to}
                  variant={blob.buttonVariant}
                  size="large"
                  startIcon={blob.buttonVariant === 'contained' ? <Add /> : <Edit />}
                  fullWidth
                >
                  {blob.buttonLabel}
                </Button>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* ── Bands ── */}
        <Box sx={{ mb: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h5" fontWeight={700}>Your Bands</Typography>
            <Button component={RouterLink} to="/submit" size="small" startIcon={<Add />}>
              Add Band
            </Button>
          </Box>

          {bands.length === 0 ? (
            <Box
              sx={{
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                p: 5,
                textAlign: 'center',
              }}
            >
              <MusicNote sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>No bands yet</Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
                Create your first band to get started.
              </Typography>
              <Button component={RouterLink} to="/submit" variant="contained" startIcon={<Add />}>
                Create Your First Band
              </Button>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {bands.map((band) => {
                const plot = stagePlotForBand(band._id);
                const total = (band.maleMembers ?? 0) + (band.femaleMembers ?? 0);
                const description = stripHtml(band.about || '');
                const pressSources = (band.externalValidation || []).filter(v => v.source).slice(0, 2);

                return (
                  <Grid item xs={12} sm={6} md={4} key={band._id}>
                    <Card
                      onClick={() => navigate(`/band/${band._id}`)}
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-6px) scale(1.01)',
                          boxShadow: '0 12px 28px rgba(0,0,0,0.15)',
                          '& .card-media': { transform: 'scale(1.08)' },
                        },
                      }}
                    >
                      {/* Image with Ken Burns zoom */}
                      <Box sx={{ position: 'relative', overflow: 'hidden', height: 180, flexShrink: 0 }}>
                        {band.mainImage ? (
                          <CardMedia
                            component="img"
                            className="card-media"
                            image={band.mainImage}
                            alt={band.artistName}
                            sx={{
                              height: '100%', width: '100%', objectFit: 'cover',
                              transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                          />
                        ) : (
                          <Box sx={{
                            height: '100%', bgcolor: 'action.hover',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <MusicNote sx={{ fontSize: 48, color: 'text.disabled' }} />
                          </Box>
                        )}
                      </Box>

                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom
                          sx={{ transition: 'color 0.3s ease', '.MuiCard-root:hover &': { color: 'primary.main' } }}>
                          {band.artistName}
                        </Typography>

                        {/* Genre chips */}
                        {band.genres && band.genres.length > 0 && (
                          <Box sx={{ mb: 1 }}>
                            {band.genres.map(genre => (
                              <Chip
                                key={genre}
                                label={genre}
                                size="small"
                                onClick={(e) => { e.stopPropagation(); }}
                                sx={{ mr: 0.5, mb: 0.5, cursor: 'default' }}
                              />
                            ))}
                          </Box>
                        )}

                        {/* Size label */}
                        {total > 0 && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {getSizeLabel(total)}
                          </Typography>
                        )}

                        {/* Description excerpt */}
                        {description && (
                          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, mb: 1 }}>
                            {description.substring(0, 120)}{description.length > 120 && '…'}
                          </Typography>
                        )}

                        {/* Press sources */}
                        {pressSources.length > 0 && (
                          <Box sx={{ mb: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {pressSources.map(v => (
                              <Chip key={v.source} label={v.source} size="small" variant="outlined"
                                sx={{ fontSize: '0.65rem', height: 20, borderColor: 'primary.light', color: 'primary.main' }} />
                            ))}
                          </Box>
                        )}

                        {/* Stage plot chip */}
                        {plot ? (
                          <Chip
                            icon={<TableChart sx={{ fontSize: '0.85rem !important' }} />}
                            label={plot.name}
                            size="small"
                            color="success"
                            variant="outlined"
                            clickable
                            onClick={(e) => { e.stopPropagation(); navigate(`/artist/stage-plots/${plot._id}/view`); }}
                            sx={{ fontSize: '0.72rem', maxWidth: '100%', alignSelf: 'flex-start' }}
                          />
                        ) : (
                          <Chip
                            icon={<Add sx={{ fontSize: '0.85rem !important' }} />}
                            label="No stage plot"
                            size="small"
                            color="warning"
                            variant="outlined"
                            clickable
                            onClick={(e) => { e.stopPropagation(); navigate(`/artist/stage-plots/new?bandId=${band._id}`); }}
                            sx={{ fontSize: '0.72rem', alignSelf: 'flex-start' }}
                          />
                        )}
                      </CardContent>

                      {/* Deactivated banner */}
                      {band.isDeactivated && (
                        <Box sx={{ px: 2, pb: 0.5 }}>
                          <Alert severity="warning" icon={<VisibilityOff fontSize="small" />} sx={{ py: 0.5, fontSize: '0.75rem' }}>
                            Deactivated — hidden from public
                          </Alert>
                        </Box>
                      )}

                      {/* Status banner */}
                      {band.isDraft && (
                        <Box sx={{ px: 2, pb: 0.5 }}>
                          <Alert
                            severity="warning"
                            icon={false}
                            sx={{ py: 0.5, fontSize: '0.75rem' }}
                            action={
                              <Button
                                size="small"
                                variant="contained"
                                color="warning"
                                startIcon={<SendIcon sx={{ fontSize: '0.8rem !important' }} />}
                                onClick={e => { e.stopPropagation(); handleSubmitForReview(band); }}
                                sx={{ fontSize: '0.7rem', py: 0.25, whiteSpace: 'nowrap' }}
                              >
                                Submit for Review
                              </Button>
                            }
                          >
                            Draft — not yet submitted
                          </Alert>
                        </Box>
                      )}
                      {!band.isDraft && band.status === 'pending' && (
                        <Box sx={{ px: 2, pb: 0.5 }}>
                          <Alert severity="info" icon={<PendingIcon fontSize="small" />} sx={{ py: 0.5, fontSize: '0.75rem' }}>
                            Awaiting admin approval (up to 24–48 hrs)
                          </Alert>
                        </Box>
                      )}
                      {!band.isDraft && band.status === 'approved' && (
                        <Box sx={{ px: 2, pb: 0.5 }}>
                          <Alert severity="success" icon={<ApprovedIcon fontSize="small" />} sx={{ py: 0.5, fontSize: '0.75rem' }}>
                            Live on imc.show
                          </Alert>
                        </Box>
                      )}
                      {!band.isDraft && band.status === 'rejected' && (
                        <Box sx={{ px: 2, pb: 0.5 }}>
                          <Alert
                            severity="error"
                            icon={<RejectedIcon fontSize="small" />}
                            sx={{ py: 0.5, fontSize: '0.75rem' }}
                            action={
                              <Button
                                size="small"
                                variant="contained"
                                color="error"
                                startIcon={<SendIcon sx={{ fontSize: '0.8rem !important' }} />}
                                onClick={e => { e.stopPropagation(); handleSubmitForReview(band); }}
                                sx={{ fontSize: '0.7rem', py: 0.25, whiteSpace: 'nowrap' }}
                              >
                                Resubmit
                              </Button>
                            }
                          >
                            Not approved — edit and resubmit
                          </Alert>
                        </Box>
                      )}

                      <CardActions sx={{ px: 1.5, pb: 1.5, pt: 0, gap: 0.5 }} onClick={e => e.stopPropagation()}>
                        <IconButton size="small" title="Edit" onClick={() => navigate(`/band/${band._id}/edit`)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        {!band.isDraft && band.status === 'approved' && (
                          <IconButton
                            size="small"
                            title={band.isDeactivated ? 'Reactivate band' : 'Deactivate band'}
                            color={band.isDeactivated ? 'success' : 'warning'}
                            onClick={(e) => handleToggleDeactivate(e, band)}
                          >
                            {band.isDeactivated ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                          </IconButton>
                        )}
                        <Box sx={{ flex: 1 }} />
                        <IconButton size="small" title="Delete" color="error" onClick={(e) => handleDeleteClick(e, band)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>

        {/* ── Appearing In ── */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>You Also Appear In</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These are bands that list you as a performer. You can view them but not edit them — speak to the band leader to make any changes.
          </Typography>

          {appearingIn.length === 0 ? (
            <Box sx={{
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
            }}>
              <Typography variant="body2" color="text.disabled">
                No other bands list you as a performer yet. When a band leader adds your name to their instrumentation, their band will appear here.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {appearingIn.map((band) => {
                const total = (band.maleMembers ?? 0) + (band.femaleMembers ?? 0);
                const description = stripHtml(band.about || '');
                const pressSources = (band.externalValidation || []).filter(v => v.source).slice(0, 2);
                return (
                  <Grid item xs={12} sm={6} md={4} key={band._id}>
                    <Card
                      onClick={() => navigate(`/band/${band._id}`)}
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                        opacity: 0.9,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-6px) scale(1.01)',
                          boxShadow: '0 12px 28px rgba(0,0,0,0.15)',
                          opacity: 1,
                          '& .card-media': { transform: 'scale(1.08)' },
                        },
                      }}
                    >
                      <Box sx={{ position: 'relative', overflow: 'hidden', height: 180, flexShrink: 0 }}>
                        {band.mainImage ? (
                          <CardMedia
                            component="img"
                            className="card-media"
                            image={band.mainImage}
                            alt={band.artistName}
                            sx={{
                              height: '100%', width: '100%', objectFit: 'cover',
                              transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                          />
                        ) : (
                          <Box sx={{
                            height: '100%', bgcolor: 'action.hover',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <MusicNote sx={{ fontSize: 48, color: 'text.disabled' }} />
                          </Box>
                        )}
                        {/* Read-only badge */}
                        <Box sx={{
                          position: 'absolute', top: 8, left: 8,
                          px: 1, py: 0.25, borderRadius: 1,
                          bgcolor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
                        }}>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                            VIEW ONLY
                          </Typography>
                        </Box>
                      </Box>

                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom
                          sx={{ transition: 'color 0.3s ease', '.MuiCard-root:hover &': { color: 'primary.main' } }}>
                          {band.artistName}
                        </Typography>
                        {band.genres && band.genres.length > 0 && (
                          <Box sx={{ mb: 1 }}>
                            {band.genres.map(genre => (
                              <Chip key={genre} label={genre} size="small"
                                onClick={e => e.stopPropagation()}
                                sx={{ mr: 0.5, mb: 0.5, cursor: 'default' }} />
                            ))}
                          </Box>
                        )}
                        {total > 0 && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {getSizeLabel(total)}
                          </Typography>
                        )}
                        {description && (
                          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, mb: 1 }}>
                            {description.substring(0, 120)}{description.length > 120 && '…'}
                          </Typography>
                        )}
                        {pressSources.length > 0 && (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {pressSources.map(v => (
                              <Chip key={v.source} label={v.source} size="small" variant="outlined"
                                sx={{ fontSize: '0.65rem', height: 20, borderColor: 'primary.light', color: 'primary.main' }} />
                            ))}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>

        {/* ── Applications ── */}
        {applications.length > 0 && (
          <Box sx={{ mb: 5 }}>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Your Applications</Typography>
            <List disablePadding>
              {applications.map((application) => {
                const isPastDeadline = new Date(application.deadline) < new Date();
                return (
                  <ListItem
                    key={application._id}
                    sx={{
                      border: '1px solid',
                      borderColor: isPastDeadline ? 'warning.dark' : 'divider',
                      borderRadius: 1,
                      mb: 1,
                      opacity: isPastDeadline ? 0.75 : 1,
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {`${application.type.charAt(0).toUpperCase() + application.type.slice(1)} Application`}
                          {isPastDeadline && (
                            <Chip label="Past Deadline" size="small" color="warning" />
                          )}
                        </Box>
                      }
                      secondary={
                        <>
                          Status: {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                          {' · '}
                          {application.status === 'draft'
                            ? `Last saved: ${new Date(application.lastSaved!).toLocaleDateString()}`
                            : `Submitted: ${new Date(application.submittedAt!).toLocaleDateString()}`}
                          {' · '}
                          Deadline: {new Date(application.deadline).toLocaleDateString()}
                        </>
                      }
                    />
                    <Box sx={{ display: 'flex', gap: 1, ml: 1, flexShrink: 0 }}>
                      {!isPastDeadline && (
                        <Button
                          variant="contained"
                          size="small"
                          component={RouterLink}
                          to={`/${application.type}/apply${application.status === 'draft' ? `?draft=${application._id}` : ''}`}
                        >
                          {application.status === 'draft' ? 'Continue' : 'View'}
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        onClick={() => handleDeleteApplicationClick(application)}
                      >
                        Delete
                      </Button>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}

        {/* ── Opportunities ── */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Open Opportunities</Typography>
          <AvailableOpportunities />
        </Box>

      </Container>

      {/* ── Application delete dialog ── */}
      <Dialog open={appDeleteDialogOpen} onClose={() => setAppDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Delete this application?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You may have spent significant time on this. Please read before continuing.
          </Alert>
          <DialogContentText sx={{ mb: 1 }}>
            You are about to delete your{' '}
            <strong>{appToDelete?.type} application</strong>
            {appToDelete?.lastSaved && (
              <> last saved on <strong>{new Date(appToDelete.lastSaved).toLocaleDateString()}</strong></>
            )}.
          </DialogContentText>
          <DialogContentText sx={{ mb: 2 }}>
            Your application will be removed from your dashboard immediately. An administrator can recover it within <strong>7 days</strong> if you change your mind — contact the IMC team.
          </DialogContentText>
          <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
            After 7 days the data is permanently gone and cannot be restored.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAppDeleteDialogOpen(false)} variant="contained">Keep It</Button>
          <Button onClick={handleDeleteApplicationConfirm} color="error" variant="outlined">Delete Anyway</Button>
        </DialogActions>
      </Dialog>

      {/* ── Band delete dialog ── */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Delete "{bandToDelete?.artistName}"?</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>This is permanent and cannot be undone.</Alert>
          <DialogContentText sx={{ mb: 1 }}>The following will be permanently deleted:</DialogContentText>
          <Box component="ul" sx={{ mt: 1, mb: 2, pl: 2 }}>
            <Box component="li" sx={{ mb: 0.5 }}>
              <Typography variant="body2"><strong>Band profile</strong> and all its information</Typography>
            </Box>
            {((bandToDelete?.additionalImages?.length ?? 0) > 0 || bandToDelete?.mainImage) && (
              <Box component="li" sx={{ mb: 0.5 }}>
                <Typography variant="body2">
                  <strong>{1 + (bandToDelete?.additionalImages?.length ?? 0)} image{(1 + (bandToDelete?.additionalImages?.length ?? 0)) > 1 ? 's' : ''}</strong>{' '}
                  — permanently removed from storage
                </Typography>
              </Box>
            )}
            {stagePlots.filter(p => p.bandId === bandToDelete?._id).length > 0 && (
              <Box component="li" sx={{ mb: 0.5 }}>
                <Typography variant="body2">
                  <strong>{stagePlots.filter(p => p.bandId === bandToDelete?._id).length} stage plot{stagePlots.filter(p => p.bandId === bandToDelete?._id).length > 1 ? 's' : ''}</strong>{' '}
                  linked to this band
                </Typography>
              </Box>
            )}
            {(bandToDelete?.publishedTo?.length ?? 0) > 0 && (
              <Box component="li" sx={{ mb: 0.5 }}>
                <Typography variant="body2">
                  <strong>{bandToDelete?.publishedTo?.length} public strand listing{(bandToDelete?.publishedTo?.length ?? 0) > 1 ? 's' : ''}</strong>{' '}
                  — band will be removed from the public IMC.show page
                </Typography>
              </Box>
            )}
          </Box>
          {(bandToDelete?.publishedTo?.length ?? 0) > 0 && (
            <Alert severity="warning">
              This band is currently visible on IMC.show. Deleting it will remove it from all public listings immediately.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Yes, Delete Everything</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ArtistDashboard;
